import {
	GetServerSideProps, GetServerSidePropsContext, GetServerSidePropsResult, NextPage,
} from 'next';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { useCallback, useEffect } from 'react';

import { fetchCapsule } from '@/data/Capsule';
import { ContentPagePlayer } from '../../../../../../components/map/capsules/ContentPagePlayer';
import { DirectionProfiles } from '../../../../../../models/MapBox';
import { NextPageProps } from '../../../../../../typings/NextPageProps';
import { checkClient } from '../../../../../../utils/serverSide';
import { MapStore } from '../../../../../../data/MapStore';

export const ParcoursCapsulePlayer: NextPage<NextPageProps> = ({ seo, client }: NextPageProps) => {
	const router = useRouter();
	const parcoursSlug = router.query.slug as string;
	const capsuleSlug = router.query.capsuleSlug as string;
	const directionProfile: DirectionProfiles = router.query.directionProfile as DirectionProfiles || 'walking';

	useEffect(() => {
		if (client) {
			MapStore.update((s) => {
				s.mapPath = !!client.attributes.mapPath;
				s.mapWalk = !!client.attributes.mapWalk;
				s.mapBike = !!client.attributes.mapBike;
				s.mapCar = !!client.attributes.mapCar;
			});
		}
	}, [client]);

	const onBack = useCallback(() => {
		router.push({
			pathname: `/map/parcours/${parcoursSlug}/capsule/${capsuleSlug}`,
			query:    {
				directionProfile,
			},
		});
	}, [capsuleSlug, directionProfile, parcoursSlug, router]);

	return (
		<>
			<NextSeo {...seo} />
			<ContentPagePlayer onBack={onBack} capsuleSlug={capsuleSlug}/>
		</>
	);
};

export const getServerSideProps: GetServerSideProps = async (
	context: GetServerSidePropsContext,
) => {
	const nextProps = await checkClient(context, ['map'], ['common', 'caption']) as { props?: NextPageProps };

	if (!nextProps.props) {
		return nextProps as unknown as Promise<GetServerSidePropsResult<NextPageProps>>;
	}

	const { capsuleSlug } = context.params as { capsuleSlug: string };

	const capsule = await fetchCapsule(capsuleSlug, context.locale || 'fr');

	return {
		props: {
			...nextProps.props,

			seo: {
				...nextProps.props.seo,

				title:       capsule?.attributes.title,
				description: capsule?.attributes.subTitle || nextProps.props.seo.description,

				openGraph: {
					type:        'website',
					title:       capsule?.attributes.title,
					description: capsule?.attributes.subTitle || nextProps.props.seo.description,
					site_name:   nextProps.props.seo.openGraph.site_name,
					url:         nextProps.props.seo.openGraph.url,
					images:      nextProps.props.client.attributes.shareImage?.data ? [
						{
							url:    nextProps.props.client.attributes.shareImage.data.attributes.url,
							alt:    'Musair',
							width:  nextProps.props.client.attributes.shareImage.data.attributes.width,
							height: nextProps.props.client.attributes.shareImage.data.attributes.height,
						},
					] : [],
				},
			},
		},
	};
};

export default ParcoursCapsulePlayer;
